import { TIME_CONSTANTS } from './time-constants'

/**
 * Converts milliseconds since midnight into "HH:MM" format (UTC)
 * Supports 24:00 format for midnight next day (86400000 ms)
 */
export function formatTime(milliseconds: number): string {
  if (milliseconds === TIME_CONSTANTS.MILLISECONDS_PER_DAY) {
    return TIME_CONSTANTS.MIDNIGHT_24H
  }
  
  const date = new Date(milliseconds)
  const hours = date.getUTCHours().toString().padStart(2, '0')
  const minutes = date.getUTCMinutes().toString().padStart(2, '0')
  return `${hours}:${minutes}`
}

/**
 * Converts an "HH:MM" string into milliseconds since midnight
 * Supports 24:00 format for midnight next day
 */
export function parseTime(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number)
  
  if (hours === TIME_CONSTANTS.HOURS_PER_DAY && minutes === 0) {
    return TIME_CONSTANTS.MILLISECONDS_PER_DAY
  }
  
  if (hours === TIME_CONSTANTS.HOURS_PER_DAY) {
    return TIME_CONSTANTS.MILLISECONDS_PER_DAY + (minutes * TIME_CONSTANTS.MILLISECONDS_PER_MINUTE)
  }
  
  return (hours * TIME_CONSTANTS.MILLISECONDS_PER_HOUR) + (minutes * TIME_CONSTANTS.MILLISECONDS_PER_MINUTE)
}

/**
 * Validates if a time string is in valid HH:MM format
 */
export function isValidTimeFormat(timeString: string): boolean {
  if (!timeString) return false
  
  const [hoursStr, minutesStr] = timeString.split(':')
  if (!hoursStr || !minutesStr) return false
  
  const hours = parseInt(hoursStr, 10)
  const minutes = parseInt(minutesStr, 10)
  
  return (
    hours >= 0 && hours <= TIME_CONSTANTS.HOURS_PER_DAY &&
    minutes >= 0 && minutes < TIME_CONSTANTS.MINUTES_PER_HOUR &&
    !(hours === TIME_CONSTANTS.HOURS_PER_DAY && minutes > 0)
  )
}

/**
 * Checks if a time string represents 24:00 format
 */
export function is24HourFormat(timeString: string): boolean {
  return timeString === TIME_CONSTANTS.MIDNIGHT_24H
}

/**
 * Converts time string to minutes since midnight for easy comparison
 */
export function timeToMinutes(timeString: string): number {
  const [hours, minutes] = timeString.split(':').map(Number)
  
  if (hours === TIME_CONSTANTS.HOURS_PER_DAY) {
    return TIME_CONSTANTS.HOURS_PER_DAY * TIME_CONSTANTS.MINUTES_PER_HOUR + minutes
  }
  
  return hours * TIME_CONSTANTS.MINUTES_PER_HOUR + minutes
}

/**
 * Converts minutes since midnight back to HH:MM format
 */
export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / TIME_CONSTANTS.MINUTES_PER_HOUR)
  const mins = minutes % TIME_CONSTANTS.MINUTES_PER_HOUR
  
  if (hours === TIME_CONSTANTS.HOURS_PER_DAY && mins === 0) {
    return TIME_CONSTANTS.MIDNIGHT_24H
  }
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`
}